import { Link } from "react-router-dom";
import { Globe, Building2, Users, FileCheck, ArrowRight, Phone, MapPin, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQ from "@/components/FAQ";
import heroImage from "@/assets/immigration-hero.jpg";
import { generateLocalBusinessSchema } from "@/lib/seo-schemas";

interface AreaContent {
  areaName: string;
  title: string;
  description: string;
  keywords: string;
  heroSubtitle: string;
  introP1: string;
  introP2: string;
  introP3: string;
  localContextP1: string;
  localContextP2: string;
  localContextP3: string;
  faqItems: Array<{ question: string; answer: string }>;
}

const GenericAreaPage = ({ content }: { content: AreaContent }) => {
  const schema = generateLocalBusinessSchema(
    content.areaName,
    "Houston",
    `Experienced immigration attorney services in ${content.areaName}, Houston. Specializing in asylum, naturalization, visas, and deportation defense for ${content.areaName} residents and businesses.`
  );

  return (
    <div className="min-h-screen">
      <SEO
        title={content.title}
        description={content.description}
        keywords={content.keywords}
        schema={schema}
      />
      
      <Header />
      
      <Breadcrumbs
        items={[
          { label: "Areas We Serve", href: "/#areas-served" },
          { label: `${content.areaName} Immigration Attorney` }
        ]}
      />

      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl text-primary-foreground">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-6 w-6 text-accent" />
              <span className="text-lg font-semibold text-accent">Serving {content.areaName}, Houston</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg">
              Immigration Attorney in {content.areaName}
            </h1>
            <p className="text-xl mb-8 opacity-95 drop-shadow-md">
              {content.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" variant="hero" className="text-lg px-8">
                <Link to="/booking">
                  Schedule Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <a href="tel:8329303059">
                <Button size="lg" variant="outline" className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary-dark bg-white/10 backdrop-blur-sm text-lg px-8">
                  <Phone className="mr-2 h-5 w-5" />
                  (832) 930-3059
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Service Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Immigration Services for {content.areaName} Residents</h2>
            <p className="text-lg text-muted-foreground mb-6">
              {content.introP1}
            </p>
            <p className="text-lg text-muted-foreground mb-6">
              {content.introP2}
            </p>
            <p className="text-lg text-muted-foreground">
              {content.introP3}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Immigration Services for {content.areaName}</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Comprehensive immigration solutions for {content.areaName} professionals, families, and businesses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-gradient-gold-shine flex items-center justify-center mb-4">
                  <Building2 className="h-6 w-6 text-accent-foreground" />
                </div>
                <CardTitle>Employment-Based Immigration</CardTitle>
                <CardDescription className="text-base">
                  H-1B visas, L-1 intracompany transfers, E-2 investor visas, O-1 extraordinary ability visas, and employment-based green cards for {content.areaName} professionals
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-gradient-gold-shine flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-accent-foreground" />
                </div>
                <CardTitle>Family-Based Immigration</CardTitle>
                <CardDescription className="text-base">
                  Green cards for spouses, children, parents, and siblings. K-1 fiancé visas and family reunification for {content.areaName} residents
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-gradient-gold-shine flex items-center justify-center mb-4">
                  <FileCheck className="h-6 w-6 text-accent-foreground" />
                </div>
                <CardTitle>Citizenship & Naturalization</CardTitle>
                <CardDescription className="text-base">
                  U.S. citizenship applications, naturalization process guidance, citizenship tests preparation for {content.areaName} permanent residents
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-gradient-gold-shine flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-accent-foreground" />
                </div>
                <CardTitle>Deportation Defense & Asylum</CardTitle>
                <CardDescription className="text-base">
                  Removal defense, asylum applications, appeals, and emergency immigration relief for {content.areaName} families facing deportation
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Why {content.areaName} Residents Choose Daniel Albert Law</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                `Experienced immigration attorneys serving ${content.areaName}`,
                "Trilingual services: English, Spanish, Vietnamese",
                "Personalized attention for every case",
                "Proven track record of successful outcomes",
                "Family and employment immigration expertise",
                "Prompt response during business hours for urgent matters"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <span className="text-lg">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Local Context */}
      <section className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">About {content.areaName}, Houston</h2>
            <p className="text-lg text-muted-foreground mb-4">
              {content.localContextP1}
            </p>
            <p className="text-lg text-muted-foreground mb-4">
              {content.localContextP2}
            </p>
            <p className="text-lg text-muted-foreground">
              {content.localContextP3}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ items={content.faqItems} />

      {/* CTA Section */}
      <CTASection
        title={`Need Immigration Help in ${content.areaName}?`}
        description={`Contact Daniel Albert Law Firm today for comprehensive immigration legal services. Our experienced attorneys are ready to help ${content.areaName} residents with all immigration matters.`}
      />

      <Footer />
    </div>
  );
};

export default GenericAreaPage;